/**
 * Alle Daten eines Kunden an einer Stelle ändern — ein Blatt von unten (Telefon)
 * bzw. ein Fenster (Rechner), Entwurf B vom 03.11.2026. Benjamin: „Ich suche jedes
 * Mal, wenn ich Datensätze anpassen möchte, wo ich sie eintrage."
 *
 * Oben die Bereiche als Reiter, darunter die Felder. Ein gemeinsamer Stand für alle
 * Reiter: wer Telefon und Anschrift ändert und dann speichert, verliert nichts beim
 * Wechseln. Gespeichert werden nur die Bereiche, die sich geändert haben (PATCH nimmt
 * einzelne Felder); Kontakt und Ansprechpartner teilen sich `kontakte_json`.
 */
import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useUpdateKunde } from "../../hooks/usePlanung";
import { errorText } from "../../lib/invoices";
import { hatName, kontakteJson, kundentypWechseln, nameFelder } from "../../lib/kundeAnlage";
import { MONATE, denormAddr, rowToForm, type KForm, type Kommunikationskanal } from "../../lib/kundeFormular";
import type { Kunde, KundePatchBody } from "../../lib/types";
import AnredeFeld from "./AnredeFeld";
import { AnsprechpartnerFeld, KontaktwegeFeld } from "./KontaktListen";
import StrassenFeld from "./StrassenFeld";
import { ZeichenZu } from "./KundeZeichen";

export type DatenBereich = "kontakt" | "personen" | "anschrift" | "pflege" | "notiz" | "name";

export const KANAL: Record<Exclude<Kommunikationskanal, "">, string> = {
  telefon: "Telefon", mail: "E-Mail", whatsapp: "WhatsApp", telegram: "Telegram",
};

const FELDER: Record<DatenBereich, (keyof KForm)[]> = {
  kontakt: ["phone", "email", "kommunikationskanal", "website", "kontakte"],
  personen: ["kontakte"],
  anschrift: ["strasse", "plz", "ort", "rechnung_strasse", "rechnung_plz", "rechnung_ort"],
  pflege: ["pflege_monate"],
  notiz: ["notes"],
  name: ["kundentyp", "name", "anrede", "vorname", "nachname"],
};

function geaendert(a: KForm, b: KForm, bereich: DatenBereich): boolean {
  return FELDER[bereich].some((f) => JSON.stringify(a[f]) !== JSON.stringify(b[f]));
}

type Props = { kunde: Kunde; start: DatenBereich; mitPersonen: boolean; onClose: () => void };

export default function KundeDatenBlatt({ kunde, start, mitPersonen, onClose }: Props) {
  const qc = useQueryClient();
  const aendern = useUpdateKunde();
  const stand = useMemo(() => rowToForm(kunde), [kunde]);
  const rechnungVorher = Boolean(stand.rechnung_strasse || stand.rechnung_plz || stand.rechnung_ort);
  const [form, setForm] = useState<KForm>(stand);
  const [rechnungAnders, setRechnungAnders] = useState(rechnungVorher);
  const [reiter, setReiter] = useState<DatenBereich>(start);
  const [fehler, setFehler] = useState("");
  const [gespeichert, setGespeichert] = useState(false);
  const [verwerfen, setVerwerfen] = useState(false);

  const offen = (Object.keys(FELDER) as DatenBereich[]).filter((b) => geaendert(form, stand, b))
    .concat(rechnungAnders !== rechnungVorher && !geaendert(form, stand, "anschrift") ? (["anschrift"] as DatenBereich[]) : []);
  const dirty = offen.length > 0;
  const setF = <K extends keyof KForm>(k: K, v: KForm[K]) => { setForm((f) => ({ ...f, [k]: v })); setGespeichert(false); setVerwerfen(false); };

  function schliessen() {
    if (dirty && !verwerfen) { setVerwerfen(true); return; }
    onClose();
  }

  useEffect(() => {
    const zu = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); schliessen(); } };
    window.addEventListener("keydown", zu, true);
    return () => window.removeEventListener("keydown", zu, true);
  });

  function speichern() {
    if (offen.includes("name") && !hatName(form)) {
      setReiter("name");
      setFehler(form.kundentyp === "privat" ? "Vor- oder Nachname fehlt." : "Der Firmenname fehlt.");
      return;
    }
    const patch: KundePatchBody = {};
    if (offen.includes("name")) Object.assign(patch, nameFelder(form), { kundentyp: form.kundentyp, typ: form.kundentyp === "privat" ? "Privat" : "Firma" });
    if (offen.includes("kontakt")) Object.assign(patch, { phone: form.phone.trim(), email: form.email.trim(), kommunikationskanal: form.kommunikationskanal, website: form.website.trim() });
    if (offen.includes("kontakt") || offen.includes("personen")) patch.kontakte_json = kontakteJson(form.kontakte);
    if (offen.includes("anschrift")) {
      const r = rechnungAnders ? form : { rechnung_strasse: "", rechnung_plz: "", rechnung_ort: "" };
      Object.assign(patch, {
        strasse: form.strasse, plz: form.plz, ort: form.ort, lieferadresse: denormAddr(form.strasse, form.plz, form.ort),
        rechnung_strasse: r.rechnung_strasse, rechnung_plz: r.rechnung_plz, rechnung_ort: r.rechnung_ort,
        rechnungsadresse: denormAddr(r.rechnung_strasse, r.rechnung_plz, r.rechnung_ort),
      });
    }
    if (offen.includes("pflege")) {
      const pm: Record<string, string> = {};
      for (const [k, v] of Object.entries(form.pflege_monate)) pm[k] = (v || "").trim();
      Object.assign(patch, { pflege_monate_json: JSON.stringify(pm), regelmaessige_pflege: Object.keys(pm).length > 0 });
    }
    if (offen.includes("notiz")) patch.notes = form.notes;
    setFehler("");
    aendern.mutate({ id: kunde.id, patch }, {
      onSuccess: (neu) => {
        const neuKunde = { ...kunde, ...(neu as unknown as Partial<Kunde>), id: kunde.id };
        qc.setQueryData<Kunde[]>(["kunden"], (alt) => alt?.map((k) => (k.id === kunde.id ? neuKunde : k)));
        // Der gespeicherte Stand wird der neue Ausgangspunkt — sonst bliebe „geändert"
        // stehen, wo der Server nur Leerzeichen abgeschnitten hat.
        const f = rowToForm(neuKunde);
        setForm(f);
        setRechnungAnders(Boolean(f.rechnung_strasse || f.rechnung_plz || f.rechnung_ort));
        setGespeichert(true);
      },
      onError: (err) => setFehler(errorText(err) || "Konnte nicht gespeichert werden."),
    });
  }

  const reiterListe: { id: DatenBereich; label: string }[] = [
    { id: "kontakt", label: "Kontakt" },
    ...(mitPersonen ? [{ id: "personen" as const, label: "Personen" }] : []),
    { id: "anschrift", label: "Anschrift" },
    { id: "pflege", label: "Pflege" },
    { id: "notiz", label: "Notiz" },
    { id: "name", label: "Name & Art" },
  ];

  return (
    <div className="ku-blatt" role="dialog" aria-modal="true" aria-label={`Daten von ${kunde.name}`}>
      <button type="button" className="ku-blatt-schleier" aria-label="Schließen" tabIndex={-1} onClick={schliessen} />
      <section className="ku-blatt-panel">
        <span className="ku-blatt-griff" aria-hidden="true" />
        <header className="ku-blatt-kopf">
          <div>
            <p className="ku-blatt-ueber">{kunde.name}</p>
            <h2>Daten</h2>
          </div>
          {gespeichert && !dirty && <span className="ku-gespeichert" role="status">✓ gespeichert</span>}
          <button type="button" className="ku-rund" aria-label="Schließen" onClick={schliessen}><ZeichenZu /></button>
        </header>
        <div className="ku-filter ku-blatt-reiter" role="tablist" aria-label="Bereiche">
          {reiterListe.map((r) => (
            <button key={r.id} type="button" role="tab" aria-selected={reiter === r.id}
              className={"ku-chip" + (reiter === r.id ? " ist-an" : "") + (offen.includes(r.id) ? " ist-geaendert" : "")}
              onClick={() => { setReiter(r.id); setFehler(""); }}>
              {r.label}
            </button>
          ))}
        </div>

        <div className="ku-blatt-inhalt lf-form">
          {reiter === "kontakt" && (
            <>
              <div className="lf-form-zwei">
                <label className="lf-label">Telefon<input className="lf-feld" type="tel" value={form.phone} onChange={(e) => setF("phone", e.target.value)} /></label>
                <label className="lf-label">E-Mail<input className="lf-feld" type="email" value={form.email} onChange={(e) => setF("email", e.target.value)} /></label>
              </div>
              <KontaktwegeFeld kontakte={form.kontakte} onChange={(k) => setF("kontakte", k)} />
              <div className="lf-label">Am liebsten erreichbar über
                <div className="ku-filter" role="group" aria-label="Bevorzugter Kontakt">
                  {(["", "telefon", "mail", "whatsapp", "telegram"] as Kommunikationskanal[]).map((k) => (
                    <button key={k || "egal"} type="button" className={"ku-chip" + (form.kommunikationskanal === k ? " ist-an" : "")}
                      aria-pressed={form.kommunikationskanal === k} onClick={() => setF("kommunikationskanal", k)}>
                      {k ? KANAL[k] : "egal"}
                    </button>
                  ))}
                </div>
              </div>
              <label className="lf-label">Website<input className="lf-feld" type="url" placeholder="https://…" value={form.website} onChange={(e) => setF("website", e.target.value)} /></label>
            </>
          )}

          {reiter === "personen" && <AnsprechpartnerFeld kontakte={form.kontakte} onChange={(k) => setF("kontakte", k)} />}

          {reiter === "anschrift" && (
            <>
              <label className="lf-label">Straße
                <StrassenFeld value={form.strasse} onChange={(v) => setF("strasse", v)}
                  onPick={(t) => { if (t.plz) setF("plz", t.plz); if (t.ort) setF("ort", t.ort); }} inputClassName="lf-feld" />
              </label>
              <div className="lf-form-zwei is-plz">
                <label className="lf-label">PLZ<input className="lf-feld" inputMode="numeric" value={form.plz} onChange={(e) => setF("plz", e.target.value)} /></label>
                <label className="lf-label">Ort<input className="lf-feld" value={form.ort} onChange={(e) => setF("ort", e.target.value)} /></label>
              </div>
              <label className="lf-merken"><input type="checkbox" checked={rechnungAnders} onChange={(e) => { setRechnungAnders(e.target.checked); setGespeichert(false); }} />Die Rechnung geht an eine andere Anschrift</label>
              {rechnungAnders && (
                <>
                  <label className="lf-label">Rechnung — Straße
                    <StrassenFeld value={form.rechnung_strasse} onChange={(v) => setF("rechnung_strasse", v)}
                      onPick={(t) => { if (t.plz) setF("rechnung_plz", t.plz); if (t.ort) setF("rechnung_ort", t.ort); }} inputClassName="lf-feld" />
                  </label>
                  <div className="lf-form-zwei is-plz">
                    <label className="lf-label">PLZ<input className="lf-feld" inputMode="numeric" value={form.rechnung_plz} onChange={(e) => setF("rechnung_plz", e.target.value)} /></label>
                    <label className="lf-label">Ort<input className="lf-feld" value={form.rechnung_ort} onChange={(e) => setF("rechnung_ort", e.target.value)} /></label>
                  </div>
                </>
              )}
            </>
          )}

          {reiter === "pflege" && (
            <>
              <p className="lf-hinweis">Monate ankreuzen; bei Bedarf dazuschreiben, was in dem Monat ansteht.</p>
              <div className="k-ans-monate-wahl">
                {MONATE.map(([n, lang, kurz]) => {
                  const key = String(n);
                  const an = Object.prototype.hasOwnProperty.call(form.pflege_monate, key);
                  return (
                    <div key={n} className={"k-ans-monat-feld" + (an ? " ist-an" : "")}>
                      <label title={lang}>
                        <input type="checkbox" checked={an} onChange={(e) => {
                          const pm = { ...form.pflege_monate };
                          if (e.target.checked) pm[key] = pm[key] ?? ""; else delete pm[key];
                          setF("pflege_monate", pm);
                        }} />
                        {kurz}
                      </label>
                      {an && <input className="lf-feld" placeholder="Tätigkeiten …" value={form.pflege_monate[key] ?? ""}
                        onChange={(e) => setF("pflege_monate", { ...form.pflege_monate, [key]: e.target.value })} />}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {reiter === "notiz" && (
            <label className="lf-label">Interne Notiz — sieht nur das Büro
              <textarea className="lf-feld" rows={6} value={form.notes} onChange={(e) => setF("notes", e.target.value)} />
            </label>
          )}

          {reiter === "name" && (
            <>
              <div className="ku-filter" role="group" aria-label="Kundenart">
                <button type="button" className={"ku-chip" + (form.kundentyp === "privat" ? " ist-an" : "")} aria-pressed={form.kundentyp === "privat"}
                  onClick={() => { setForm((f) => kundentypWechseln(f, "privat")); setGespeichert(false); }}>Privatkunde</button>
                <button type="button" className={"ku-chip" + (form.kundentyp === "firma" ? " ist-an" : "")} aria-pressed={form.kundentyp === "firma"}
                  onClick={() => { setForm((f) => kundentypWechseln(f, "firma")); setGespeichert(false); }}>Firmenkunde</button>
              </div>
              {form.kundentyp === "firma"
                ? <label className="lf-label">Firmenname<input className="lf-feld" value={form.name} onChange={(e) => setF("name", e.target.value)} /></label>
                : <AnredeFeld anrede={form.anrede} vorname={form.vorname} nachname={form.nachname} onChange={(w) => { setForm((f) => ({ ...f, ...w })); setGespeichert(false); }} />}
            </>
          )}
        </div>

        <footer className="ku-blatt-fuss">
          {fehler && <p className="lf-fehler" role="alert">{fehler}</p>}
          {verwerfen && <p className="ku-blatt-frage" role="alert">Nicht gespeichert — trotzdem schließen?</p>}
          <div className="ku-blatt-knoepfe">
            <button type="button" className="ku-knopf" onClick={verwerfen ? onClose : schliessen}>
              {verwerfen ? "Verwerfen" : dirty ? "Abbrechen" : "Fertig"}
            </button>
            <button type="button" className="ku-knopf is-primaer" disabled={!dirty || aendern.isPending} onClick={speichern}>
              {aendern.isPending ? "Speichert …" : dirty ? `Speichern${offen.length > 1 ? ` (${offen.length} Bereiche)` : ""}` : "Gespeichert"}
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
}
